import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  searchService,
  SearchResult,
  SearchFilters,
  SearchFacets,
} from "../services/searchService";

export interface SearchState {
  isModalOpen: boolean;
  isSearching: boolean;
  searchQuery: string;
  searchResults: {
    events: SearchResult[];
    resources: SearchResult[];
    totalEvents: number;
    totalResources: number;
  };
  facets: SearchFacets;
  suggestions: SearchResult[];
  recentSearches: string[];
  selectedFilters: SearchFilters;
  error: string | null;
  currentPage: number;
  hasMore: boolean;
}

const initialState: SearchState = {
  isModalOpen: false,
  isSearching: false,
  searchQuery: "",
  searchResults: {
    events: [],
    resources: [],
    totalEvents: 0,
    totalResources: 0,
  },
  facets: {},
  suggestions: [],
  recentSearches: JSON.parse(localStorage.getItem("recentSearches") || "[]"),
  selectedFilters: {
    type: "all",
  },
  error: null,
  currentPage: 0,
  hasMore: false,
};

// Async thunks
export const performSearch = createAsyncThunk(
  "search/performSearch",
  async ({
    query,
    filters,
    page = 0,
  }: {
    query: string;
    filters?: SearchFilters;
    page?: number;
  }) => {
    const results = await searchService.searchAll(
      query,
      filters || {},
      page,
      20,
    );
    return { results, query, page };
  },
);

export const fetchSuggestions = createAsyncThunk(
  "search/fetchSuggestions",
  async (query: string) => {
    const suggestions = await searchService.getSuggestions(query, 5);
    return suggestions;
  },
);

export const initializeSearch = createAsyncThunk(
  "search/initialize",
  async (forceRefresh: boolean = true) => {
    // Force refresh to get new secured key with correct filters
    await searchService.initialize(forceRefresh);
  },
);

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    openSearchModal: (state) => {
      state.isModalOpen = true;
    },
    closeSearchModal: (state) => {
      state.isModalOpen = false;
      state.searchQuery = "";
      state.searchResults = {
        events: [],
        resources: [],
        totalEvents: 0,
        totalResources: 0,
      };
      state.facets = {};
      state.suggestions = [];
      state.currentPage = 0;
      state.error = null;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSearchFilter: (state, action: PayloadAction<Partial<SearchFilters>>) => {
      state.selectedFilters = {
        ...state.selectedFilters,
        ...action.payload,
      };
      // Reset page when filters change
      state.currentPage = 0;
    },
    clearFilters: (state) => {
      state.selectedFilters = { type: "all" };
      state.currentPage = 0;
    },
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const query = action.payload.trim();
      if (query && !state.recentSearches.includes(query)) {
        state.recentSearches = [
          query,
          ...state.recentSearches.slice(0, 9), // Keep max 10 recent searches
        ];
        localStorage.setItem(
          "recentSearches",
          JSON.stringify(state.recentSearches),
        );
      }
    },
    removeRecentSearch: (state, action: PayloadAction<string>) => {
      state.recentSearches = state.recentSearches.filter(
        (search) => search !== action.payload,
      );
      localStorage.setItem(
        "recentSearches",
        JSON.stringify(state.recentSearches),
      );
    },
    clearRecentSearches: (state) => {
      state.recentSearches = [];
      localStorage.removeItem("recentSearches");
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize search
      .addCase(initializeSearch.pending, (state) => {
        state.error = null;
      })
      .addCase(initializeSearch.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(initializeSearch.rejected, (state, action) => {
        state.error = action.error.message || "Failed to initialize search";
      })

      // Perform search
      .addCase(performSearch.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        const { results, query, page } = action.payload;

        if (page === 0) {
          // New search, replace results
          state.searchResults = {
            events: results.events,
            resources: results.resources,
            totalEvents: results.totalEvents,
            totalResources: results.totalResources,
          };
          // Update facets with new search
          state.facets = results.facets;
        } else {
          // Pagination, append results
          state.searchResults.events = [
            ...state.searchResults.events,
            ...results.events,
          ];
          state.searchResults.resources = [
            ...state.searchResults.resources,
            ...results.resources,
          ];
          state.searchResults.totalEvents = results.totalEvents;
          state.searchResults.totalResources = results.totalResources;
          // Keep existing facets for pagination
        }

        state.currentPage = page;

        // Check if there are more results
        const currentTotal =
          state.searchResults.events.length +
          state.searchResults.resources.length;
        const maxTotal = results.totalEvents + results.totalResources;
        state.hasMore = currentTotal < maxTotal;

        state.isSearching = false;

        // Add to recent searches if it's a new search (not pagination)
        if (page === 0 && query.trim()) {
          const recentQuery = query.trim();
          if (!state.recentSearches.includes(recentQuery)) {
            state.recentSearches = [
              recentQuery,
              ...state.recentSearches.slice(0, 9),
            ];
            localStorage.setItem(
              "recentSearches",
              JSON.stringify(state.recentSearches),
            );
          }
        }
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.error.message || "Search failed";
      })

      // Fetch suggestions
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        state.suggestions = action.payload;
      })
      .addCase(fetchSuggestions.rejected, (state) => {
        state.suggestions = [];
      });
  },
});

export const {
  openSearchModal,
  closeSearchModal,
  setSearchQuery,
  setSearchFilter,
  clearFilters,
  addRecentSearch,
  removeRecentSearch,
  clearRecentSearches,
  clearError,
} = searchSlice.actions;

export default searchSlice.reducer;
